import MainLayout from "../components/MainLayout";
import Typography from "../components/Typography";
import SectionTitle from "../components/SectionTitle";
import Table from "../components/Table";
import { formatNumber } from "../utils/formatNumber";
import { useNavigate } from "react-router-dom";
import { appPaths } from "../Routes";
import { SectorReport, usePaginatedSectorReport } from "../api/reports";
import {
    ArrowLeft,
    ArrowRight,
    Calendar,
    ChartLine,
    Graph,
    Scales,
} from "@phosphor-icons/react";
import IconButton from "../components/IconButton";
import ContextMenu from "../components/ContextMenu";

const ReportsPage = () => {
    const { isLoading, sectorReports, year, setYear } =
        usePaginatedSectorReport();

    const rows = sectorReports.map((report) => ({
        ...report,
    }));

    return (
        <MainLayout>
            <div className="py-8 px-5 flex flex-col gap-6 md:gap-0 md:flex-row md:items-center md:justify-between">
                <Typography
                    variant="display-small"
                    component="h1"
                    className="text-deep-ocean-20"
                >
                    Reports
                </Typography>
            </div>
            <div className="p-6 mt-2">
                <SectionTitle title="Financed Emissions" />
                <div className="flex flex-row gap-3 items-center">
                    <Calendar />
                    <IconButton
                        icon={ArrowLeft}
                        label={""}
                        onClick={() => setYear(year - 1)}
                    />
                    {year}
                    <IconButton
                        icon={ArrowRight}
                        label={""}
                        onClick={() => setYear(year + 1)}
                    />
                </div>
                <div className="mt-4">
                    <SectorReportTable
                        isLoading={isLoading}
                        rows={rows}
                        year={year}
                    />
                </div>
            </div>
        </MainLayout>
    );
};

type SectorReportTableProps = {
    rows: SectorReportTableRow[];
    isLoading: boolean;
    year: number;
};

type SectorReportTableRow = SectorReport;

const SectorReportTable = ({
    rows,
    isLoading,
    year,
}: SectorReportTableProps) => {
    const navigate = useNavigate();

    const getMenuItems = (sector: string, scopes: number[]) => {
        return [
            {
                onClick: () =>
                    navigate(
                        appPaths.calculationVeracity(
                            sector,
                            scopes.join(","),
                            year.toString()
                        )
                    ),
                label: "Origin",
                icon: Graph,
            },
            {
                onClick: () =>
                    navigate(
                        appPaths.financedEmissionsHistory(
                            sector,
                            scopes.join(","),
                            year.toString()
                        )
                    ),
                label: "History",
                icon: ChartLine,
            },
            {
                onClick: () => {
                    return undefined;
                },
                label: "Methodology",
                icon: Scales,
            },
        ];
    };

    if (isLoading)
        return (
            <div className="flex flex-col items-center">
                <p className="mt-2 opacity-60">Loading...</p>
            </div>
        );
    return (
        <Table
            className="!min-w-[1200px]"
            headings={[
                {
                    label: "Sector",
                },
                { label: "Scope 1/2 Financed Emissions (kg CO2e)" },
                { label: "Scope 1/2 PCAF Score" },
                { label: "Scope 3 Financed Emissions (kg CO2e)" },
                { label: "Scope 3 PCAF Score" },
                { label: "Financed Emissions Intensity (kg CO2e/£m)"}
            ]}
            data={rows}
            renderRowCells={(rowData) => (
                <>
                    <td className="min-w-[55px]">{rowData.name}</td>
                    <ContextMenu
                        menuItems={getMenuItems(
                            rowData.id,
                            [1,2]
                        )}
                        component="td"
                    >
                        {formatNumber(rowData.scopeOneAndTwoFinancedEmissions)}
                    </ContextMenu>
                    <td>
                        {formatNumber(rowData.scopeOneAndTwoWeightedPCAFScore)}
                    </td>
                    <ContextMenu
                        menuItems={getMenuItems(
                            rowData.id,
                            [3]
                        )}
                        component="td"
                    >
                        {formatNumber(rowData.scopeThreeFinancedEmissions)}
                    </ContextMenu>
                    <td>
                        {formatNumber(rowData.scopeThreeWeightedPCAFScore)}
                    </td>
                    <td>
                        {formatNumber(rowData.financedEmissionsIntensity * 10e6)}
                    </td>
                </>
            )}
        />
    );
};

export default ReportsPage;
